import { useState, useEffect } from 'react';
import { enrollmentAPI, handleApiError } from '../../../utils/api';
import { useApiErrorHandler } from '../../../utils/apiErrorHandler';

/**
 * Custom hook for managing regions data
 * Updated to use centralized API client following documentation guidelines
 */
const useRegions = () => {
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { showError } = useApiErrorHandler();

  const fetchRegions = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await enrollmentAPI.getRegions();
      setRegions(response.data);
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const createRegion = async regionData => {
    try {
      setLoading(true);
      setError(null);

      const response = await enrollmentAPI.createRegion(regionData);
      setRegions(prev => [...prev, response.data]);
      return { success: true, data: response.data };
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
      showError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const updateRegion = async (id, regionData) => {
    try {
      setLoading(true);
      setError(null);

      const response = await enrollmentAPI.updateRegion(id, regionData);
      setRegions(prev =>
        prev.map(region => (region.id === id ? response.data : region))
      );
      return { success: true, data: response.data };
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
      showError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const deleteRegion = async id => {
    try {
      setLoading(true);
      setError(null);

      await enrollmentAPI.deleteRegion(id);
      setRegions(prev => prev.filter(region => region.id !== id));
      return { success: true };
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
      showError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRegions();
  }, []);

  return {
    regions,
    loading,
    error,
    fetchRegions,
    createRegion,
    updateRegion,
    deleteRegion,
  };
};

export default useRegions;
